import * as angular from 'angular';
import * as _ from 'lodash';

import { guidEmpty, WideError } from '../../common';
import {
    VariableType,
    ConnectionPoint,
    TagDefinition,
    TagDefinitionDTO,
    GroupNameDto,
    IFrequency,
    ITagStatus,
    TagStatus,
    ITagsStatusCount,
    TagDefinitionVm,
    VariableSubType,
    Qualifier,
    Unit,
    ActualDataType,
    ErrorLevel,
    AcquisitionStrategy,
    IValueViewModel,
    Dictionary,
    DigitalSet
} from '../../models';
import { ICurrentApplicationService, IVariableCascadingService, IFrequencyFormatHelperService } from '../../services';
import { validateTagConnectionPoint } from './validateTagConnectionPoint';

export interface ITagDefinitionMapper {
    checkTagsStatus(tags: TagDefinitionVm[], variableTypesDictionary: Dictionary<VariableType>): ITagsStatusCount;
    mapTagsVm(
        tags: TagDefinitionDTO[],
        connectionsPoints: ConnectionPoint[],
        variableTypesDictionary: Dictionary<VariableType>,
        variableSubTypesDictionary: Dictionary<VariableSubType>
    ): TagDefinitionVm[];
    mapTagsWithAll(
        tags: TagDefinitionDTO[],
        connectionPoints: Dictionary<ConnectionPoint>,
        variableTypes: Dictionary<VariableType>,
        variableSubTypes: Dictionary<VariableSubType>,
        qualifiers: Dictionary<Qualifier>,
        units: Dictionary<Unit>,
        viewModels: Dictionary<IValueViewModel>,
        digitalSets: Dictionary<DigitalSet>
    ): TagDefinitionVm[];
}

export class TagDefinitionMapper implements ITagDefinitionMapper {
    static serviceId = 'tagDefinitionMapper';

    static $inject = [
        '$translate',
        'variableCascadingService',
        'frequencyFormatHelperService',
        'currentApplicationService'
    ];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _variableCascadingService: IVariableCascadingService,
        private _frequencyFormatHelperService: IFrequencyFormatHelperService,
        private _currentApplicationService: ICurrentApplicationService
    ) {
    }

    mapTagsVm(tags: TagDefinitionDTO[], connectionsPoints: ConnectionPoint[], variableTypesDictionary: Dictionary<VariableType>, variableSubTypesDictionary: Dictionary<VariableSubType>): TagDefinitionVm[] {
        const result = tags.map(t => this.mapTagVm(t, connectionsPoints, variableTypesDictionary, variableSubTypesDictionary));
        return result;
    }

    mapTagsWithAll(
        tags: TagDefinitionDTO[],
        connectionPoints: Dictionary<ConnectionPoint>,
        variableTypes: Dictionary<VariableType>,
        variableSubTypes: Dictionary<VariableSubType>,
        qualifiers: Dictionary<Qualifier>,
        units: Dictionary<Unit>,
        viewModels: Dictionary<IValueViewModel>,
        digitalSets: Dictionary<DigitalSet>
    ): TagDefinitionVm[] {
        return tags.map(tag => {
            const td = tag.tagDefinition;
            const variableType = variableTypes[td.variableTypeGuid];
            const t = this.mapCommon(tag, variableType);
            t.errors = (t.errors || []).concat(validateTagConnectionPoint(t, connectionPoints));
            t.connectionPointName = connectionPoints[td.acquisitionPointGuid]?.name ?? '';
            t.variableTypeName = td.actualDataType === ActualDataType.Digital
                ? digitalSets[td.digitalSetGuid]?.name
                : variableType?.name ?? '';
            t.unitName = units[td.unitGuid]?.name ?? '';
            t.sourceUnitName = units[td.sourceUnitGuid]?.name ?? '';
            t.subtypeName = variableSubTypes[td.variableSubTypeGuid]?.name ?? '';
            t.qualifierName = qualifiers[td.qualifierGuid]?.name ?? '';
            t.isUsed = !viewModels[tag.tagDefinition.guid].canDelete;
            return t;
        })
            .sort((a, b) => a.name < b.name ? -1 : 1);
    }

    /**
     * Validate a list of tagDTO and return a general status for all tags (ITagsStatus)
     * @param tagsDto List of tagDTO
     */
    checkTagsStatus(tagsVm: TagDefinitionVm[], variableTypesDictionary: Dictionary<VariableType>): ITagsStatusCount {
        const tagsStatus: ITagsStatusCount = {
            invalid: 0,
            requireReview: 0,
            ignored: 0,
            total: tagsVm.length
        };
        tagsVm.forEach(tagVm => {
            let tagStatus: ITagStatus = {
                status: TagStatus.VALID,
                errors: []
            };
            if (!tagVm.isIgnored) {
                const variable = variableTypesDictionary[tagVm.variableTypeGuid];
                tagStatus = this.validateTagStatus(tagStatus, tagVm, variable);
                tagStatus = this.getErrorCondition(tagStatus, tagVm, tagVm.groupList);
            } else {
                tagStatus.status = TagStatus.IGNORED;
            }
            switch (tagStatus.status) {
                case TagStatus.INVALID:
                    tagsStatus.invalid++;
                    break;
                case TagStatus.REQUIRE_REVIEW:
                    tagsStatus.requireReview++;
                    break;
                case TagStatus.IGNORED:
                    tagsStatus.ignored++;
                    break;
            }
        });
        return tagsStatus;
    }

    // /!\ this method should only be called if cascadingVariableService and connectionPoint list are ready !
    private mapTagVm(
        tag: TagDefinitionDTO,
        connectionPoints: ConnectionPoint[],
        variableTypesDictionary: Dictionary<VariableType>,
        variableSubTypesDictionary: Dictionary<VariableSubType>
    ): TagDefinitionVm {
        const variable = variableTypesDictionary[tag.tagDefinition.variableTypeGuid];
        const t = this.mapCommon(tag, variable);
        const cpDic = {};
        connectionPoints.forEach(cp => cpDic[cp.guid] = cp);
        t.errors = (t.errors || []).concat(validateTagConnectionPoint(t, cpDic));
        t.connectionPointName = this.getConnectionPointName(t.acquisitionPointGuid, connectionPoints);
        t.variableTypeName = this._variableCascadingService.getVariableNameFromDictionary(t.variableTypeGuid, variableTypesDictionary);
        t.unitName = this._variableCascadingService.getVariableUnitNameFromDictionary(t.variableTypeGuid, t.unitGuid, variableTypesDictionary);
        t.sourceUnitName = this._variableCascadingService.getVariableUnitNameFromDictionary(t.variableTypeGuid, t.sourceUnitGuid, variableTypesDictionary);
        t.subtypeName = this._variableCascadingService.getVariableSubTypeNameFromDictionary(t.variableTypeGuid, t.variableSubtypeGuid, variableTypesDictionary, variableSubTypesDictionary);
        t.qualifierName = this._variableCascadingService.getVariableQualifierNameFromDictionary(t.variableTypeGuid, t.qualifierGuid, variableTypesDictionary);
        return t;
    }

    private mapCommon(tag: TagDefinitionDTO, variable: VariableType): TagDefinitionVm {
        const t: TagDefinitionVm = tag.tagDefinition;
        t.groupList = tag.groups;
        t.isUsed = t.groupList && t.groupList.length > 0;
        t.identifierNames = tag.identifierNames;
        t.errors = this.validateTag(tag, variable);
        t.type = t.variableName;
        t.unit = t.unitName;
        t.displayComment = t.comment
            ? t.comment
            : t.shortComment
                ? t.shortComment
                : t.longComment
                    ? t.longComment.substring(0, 16)
                    : '';
        return t;
    }

    private validateTag(tagDTO: TagDefinitionDTO, type: VariableType): WideError[] {
        const t: any = tagDTO.tagDefinition;
        t.errors = this.validate(tagDTO, type);
        t.errors.concat(this.checkErrorCondition(tagDTO));
        return t.errors;
    }

    /**
     * Check error condition
     * @param tag Tag DTO
     */
    private checkErrorCondition(tag: TagDefinitionDTO): WideError[] {
        const t: TagDefinition & { errors: WideError[] } = tag.tagDefinition as any;
        if (!t.errors) {
            t.errors = [];
        }
        let tagStatus: ITagStatus = {
            status: TagStatus.VALID,
            errors: []
        };
        tagStatus = this.getErrorCondition(tagStatus, t, tag.groups);
        tagStatus.errors.forEach(error => {
            t.errors.push(error);
        });
        return t.errors;
    }

    /**
     * Validate a tag
     * @param tag tagDefinition to validate
     * @param type Variable type
     */
    private validate(tagDTO: TagDefinitionDTO, type: VariableType): WideError[] {
        let tagStatus: ITagStatus = {
            status: TagStatus.VALID,
            errors: []
        };
        tagStatus = this.validateTagStatus(tagStatus, tagDTO.tagDefinition, type);
        // TODO future : check LL/HH for warning
        // Do this in validateTagStatus method below
        return tagStatus.errors;
    }

    /**
     * Validate tag and get status (extracted validate method)
     * @param tagStatus Tag status object with status and error list
     * @param tag Tag definition to validate
     * @param type Variable type
     */
    private validateTagStatus(tagStatus: ITagStatus, tag: TagDefinition, type: VariableType): ITagStatus {
        if (tag) {
            if (tag.actualDataType === ActualDataType.Float) {
                if (this.emptyGuid(tag.variableTypeGuid)) {
                    tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('A variable type is required')));
                }
                if (!type) {
                    tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('Unknown variable type')));
                }
                if (type) {
                    if (type.qualifierCollection.length > 0 && this.emptyGuid(tag.qualifierGuid)) {
                        tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('A qualifier is required')));
                    }

                    if (type.variableSubTypeGuidCollection.length > 0 && this.emptyGuid(tag.variableSubtypeGuid)) {
                        tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('A variable subtype is required')));
                    }
                    const unitGuids = type.unitCollection.map(u => u.guid);
                    if (unitGuids.indexOf(tag.unitGuid) < 0) {
                        tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('Unit is invalid')));
                    }
                    if (tag.sourceUnitGuid !== guidEmpty && unitGuids.indexOf(tag.sourceUnitGuid) < 0) {
                        tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('Source unit is invalid')));
                    }
                }
                if (this.emptyGuid(tag.unitGuid)) {
                    tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('A unit is required')));
                }
            } else if (tag.actualDataType === ActualDataType.Digital) {
                if (!tag.digitalSetGuid || tag.digitalSetGuid === guidEmpty) {
                    tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('A digital set is required')));
                }
            }

            if (this.emptyGuid(tag.acquisitionPointGuid)) {
                tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('A source is required')));
            }

            if (tag.isReviewRequired) {
                tagStatus.errors.push(new WideError(ErrorLevel.Warning, this._$translate.instant('Requires review')));
            }
        }
        return tagStatus;
    }

    /**
     * Check error condition (extracted checkErrorCondition method)
     * @param tagStatus Tag status object with status and error list
     * @param tag Tag definition to validate
     * @param groups groups where the tag is used
     */
    private getErrorCondition(tagStatus: ITagStatus, tag: TagDefinition, groups: GroupNameDto[]): ITagStatus {
        const groupedBy = _.chain(groups.map(g => this.getFrequency(g)))
            .groupBy(f => this._frequencyFormatHelperService.formatFrequency(f));
        const frequencies: IFrequency[] = [].concat.apply([], groupedBy
            .values()
            .value());
        const keys: IFrequency[] = [].concat.apply([], groupedBy
            .keys()
            .value());
        if (keys.length > 1) {
            tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('The tag is used in groups using different frequencies')));
        } else if (tag.acquisitionStrategy === AcquisitionStrategy.PreviousStep) {
            if (!tag.validityInterval || tag.validityInterval === 0) {
                tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('Previous step tag should have a validity period')));
            } else {
                const freq = (frequencies.length === 0 || frequencies[0].interval === 0)
                    ? this.getDefaultFrequency()
                    : frequencies[0];

                const tagFreq = this._frequencyFormatHelperService.toDuration(freq).asSeconds();
                const tagValidity = this._frequencyFormatHelperService.toDuration({
                    interval: tag.validityInterval,
                    intervalTimeUnit: tag.validityUnit
                }).asSeconds();

                if (tagValidity >= tagFreq) {
                    tagStatus.errors.push(new WideError(ErrorLevel.Error, this._$translate.instant('Validity period of the tag is longer than the acquisition frequency')));
                }
            }
        }
        const onlyErrors = tagStatus.errors.filter(e => e.level === ErrorLevel.Error || e.level === ErrorLevel.Fatal);
        if (onlyErrors && onlyErrors.length === 0) {
            if (tag.isReviewRequired) {
                tagStatus.status = TagStatus.REQUIRE_REVIEW;
            }
            else if (tag.isIgnored) {
                tagStatus.status = TagStatus.IGNORED;
            }
        } else {
            tagStatus.status = TagStatus.INVALID;
        }

        return tagStatus;
    }

    private getFrequency(group: GroupNameDto): IFrequency {
        return group.frequency.interval === 0
            ? this.getDefaultFrequency()
            : group.frequency;
    }

    private getConnectionPointName(guid: string, connectionPoints: ConnectionPoint[]) {
        let name: string;
        if (guid === guidEmpty) {
            name = '';
        } else {
            const connectionPoint = _.find(connectionPoints, c => c.guid === guid);
            name = connectionPoint?.name ?? '';
        }
        return name;
    }

    private getDefaultFrequency(): IFrequency {
        const conf = this._currentApplicationService.getApplicationConfiguration();
        return {
            interval: conf.interval,
            intervalTimeUnit: conf.intervalTimeUnit
        };
    }

    private emptyGuid(guid: string): boolean {
        return !guid || guid === guidEmpty;
    }
}